import { type ReactNode, useId } from 'react'
import {
  CardItemContent,
  CardItemIcon,
  CardList,
  CardListEmptyState,
  CardListItem,
  CardSubtitle,
  CardTitle,
} from '@/components/Card'
import {
  Dropdown,
  DropdownItem,
  DropdownItemDescription,
  DropdownItemSeparator,
  DropdownItemTitle,
} from '@/components/Dropdown'
import { IconButton } from '@/components/IconButton'
import { SectionHeadline } from '@/components/SectionHeadline'
import { Button } from '@/components/Button'
import flattenChildren from 'react-keyed-flatten-children'
import { AutoSkeleton, useIsSkeleton } from '@/components/Skeleton'

/**
 * Props for {@link DependentProfileCardListSection}.
 */
export interface DependentProfileCardListSectionProps {
  /**
   * The children to render (the list items).
   */
  children?: ReactNode

  /**
   * Optional action for the section header.
   */
  action?: ReactNode

  /**
   * Render the empty state.
   */
  renderEmptyState?: () => ReactNode

  /**
   * Whether to render as a skeleton.
   */
  isSkeleton?: boolean

  /**
   * Test ID attribute.
   */
  testId?: string
}

/**
 * A section within the dependent profile list.
 */
export function DependentProfileCardListSection({
  children,
  action,
  renderEmptyState,
  isSkeleton,
  testId,
}: DependentProfileCardListSectionProps) {
  isSkeleton = useIsSkeleton(isSkeleton)
  const headlineId = useId()
  return (
    <section className="pb-lg" data-test={testId}>
      <SectionHeadline id={headlineId} isSkeleton={isSkeleton} action={action}>
        Dependents
      </SectionHeadline>
      <CardList
        aria-labelledby={headlineId}
        renderEmptyState={renderEmptyState}
      >
        {children}
      </CardList>
    </section>
  )
}

/**
 * Dependent Profile Card List Section Skeleton
 */
export function DependentProfileCardListSectionSkeleton() {
  return (
    <DependentProfileCardListSection isSkeleton>
      <ProvidedDependentProfileCardListItem
        fullName="Placeholder"
        relationship="Placeholder"
        isSkeleton
      />
    </DependentProfileCardListSection>
  )
}

interface ProvidedDependentProfileCardListItemProps {
  fullName: string
  relationship: string
  onAction?: () => unknown
  isDisabled?: boolean
  onRemovePress?: () => unknown
  isReadOnly?: boolean
  isSkeleton?: boolean
  testId?: string
}

/**
 * A card in the dependent profiles list.
 */
export function ProvidedDependentProfileCardListItem({
  fullName,
  relationship,
  onAction,
  isDisabled,
  onRemovePress,
  isReadOnly,
  isSkeleton,
  testId,
}: ProvidedDependentProfileCardListItemProps) {
  isSkeleton = useIsSkeleton(isSkeleton)
  const textValue = `${fullName} (${relationship})`
  return (
    <CardListItem
      isDisabled={isDisabled || isSkeleton}
      textValue={isSkeleton ? 'Placeholder' : textValue}
      onAction={isSkeleton ? undefined : onAction}
      testId={testId}
    >
      <CardItemContent
        isSkeleton={isSkeleton}
        icon={<CardItemIcon skeleton={isSkeleton} name="dependent" />}
        extras={
          !isSkeleton && (onAction || onRemovePress) ? (
            <Dropdown
              title="Actions"
              trigger={
                <IconButton
                  isDisabled={isDisabled}
                  sm
                  variant="no-hover"
                  name="more"
                  label="More actions"
                  testId={testId ? `${testId}-menu-button` : undefined}
                />
              }
            >
              {onAction && (
                <DropdownItem
                  icon="edit"
                  onAction={onAction}
                  testId={testId ? `${testId}-view-edit` : undefined}
                >
                  {isReadOnly ? 'View dependent' : 'View/edit dependent'}
                </DropdownItem>
              )}
              {onRemovePress && !isReadOnly && (
                <DropdownItem
                  icon="remove"
                  onAction={onRemovePress}
                  testId={testId ? `${testId}-remove` : undefined}
                >
                  Remove dependent
                </DropdownItem>
              )}
            </Dropdown>
          ) : undefined
        }
      >
        <CardTitle>
          <AutoSkeleton isSkeleton={isSkeleton}>{fullName}</AutoSkeleton>
        </CardTitle>
        <CardSubtitle variant="sm-description">
          <AutoSkeleton isSkeleton={isSkeleton}>{relationship}</AutoSkeleton>
        </CardSubtitle>
      </CardItemContent>
    </CardListItem>
  )
}

interface ProvidedDependentProfileCardListEmptyStateProps {
  action?: ReactNode
  testId?: string
}

/**
 * Provided Dependent Profile Card List Empty State
 */
export function ProvidedDependentProfileCardListEmptyState({
  action,
  testId,
}: ProvidedDependentProfileCardListEmptyStateProps) {
  return (
    <CardListEmptyState
      action={action}
      title={
        action
          ? 'Do you have any dependents to claim?'
          : 'No dependents claimed.'
      }
      testId={testId}
    />
  )
}

interface AddDependentButtonProps {
  /**
   * Instances of {@link ExistingDependentItem} for displaying a dropdown
   * of options to add existing dependents.
   */
  children?: ReactNode
  /**
   * Called when the user wants to add a new dependent.
   */
  onAddNewPress?: () => unknown
  /**
   * Whether the button is disabled.
   */
  isDisabled?: boolean
  /**
   * Test ID attribute.
   */
  testId?: string
}

/**
 * Add Dependent Button
 */
export function AddDependentButton({
  onAddNewPress,
  children,
  isDisabled,
  testId,
}: AddDependentButtonProps) {
  const flattenedChildren = flattenChildren(children)
  const isEmpty = flattenedChildren.length === 0
  const button = (
    <Button
      label="Add dependent"
      variant="text"
      onPress={isEmpty ? onAddNewPress : undefined}
      isDisabled={isDisabled}
      testId={testId}
    />
  )

  if (isEmpty) {
    return button
  }

  return (
    <Dropdown trigger={button} title="Dependents">
      {children}
      <DropdownItemSeparator />
      <DropdownItem
        onAction={onAddNewPress}
        icon="add"
        testId={testId ? `${testId}-add-new` : undefined}
      >
        Add new dependent
      </DropdownItem>
    </Dropdown>
  )
}

interface ExistingDependentItemProps {
  fullName: string
  relationship: string
  onAction?: () => unknown
}

/**
 * An existing dependent that can be added.
 * Used as children in {@link AddDependentButton}.
 */
export function ExistingDependentItem({
  fullName,
  onAction,
  relationship,
}: ExistingDependentItemProps) {
  const textValue = `${fullName} (${relationship})`
  return (
    <DropdownItem icon="dependent" textValue={textValue} onAction={onAction}>
      <DropdownItemTitle>{fullName}</DropdownItemTitle>
      <DropdownItemDescription>{relationship}</DropdownItemDescription>
    </DropdownItem>
  )
}
